import {
  resolveCategoryColor,
  toPaletteColorName,
} from '@/features/colors/lib/paletteLookup';
import type { PageExperience } from '@/features/experiences/types';
import { useDocumentContext } from '@/features/printables/contexts/DocumentContext';
import { FONT_TEXT } from '@/features/printables/css/customFonts';
import { getStyleById } from '@/features/printables/theme';
import { useIsMobileBreakpoint } from '@/hooks/useIsBreakpoint';
import { ExperienceColorMode } from '@/types/gql.generated';
import { Flex } from '@/ui';
import { FeedIndicator } from '../../shared/FeedIndicator';
import { LegendRow } from '../../shared/LegendRow';

type Props = {
  experience: PageExperience;
};

export const Legend = ({ experience }: Props) => {
  const { isPrintEnv } = useDocumentContext();
  const isMobile = useIsMobileBreakpoint();
  const stackedMobile = !isPrintEnv && isMobile;
  const theme = getStyleById(experience.styleId ?? undefined);

  return (
    <Flex
      alignItems={stackedMobile ? 'center' : 'flex-end'}
      color={theme.neutral3}
      columnGap={stackedMobile ? '1em' : '0.25em'}
      flexDirection={stackedMobile ? 'row' : 'column'}
      flexShrink={0}
      flexWrap={stackedMobile ? 'wrap' : 'nowrap'}
      fontFamily={FONT_TEXT}
      fontSize={stackedMobile ? '0.85em' : '0.75em'}
      h={stackedMobile ? 'auto' : '100%'}
      justifyContent={stackedMobile ? 'flex-start' : 'flex-end'}
      letterSpacing="0.025em"
      pb={stackedMobile ? 0 : '1em'}
      rowGap={stackedMobile ? '0.25em' : undefined}
    >
      {/* Legend dots render small, so the indicator always uses the bold (500)
          color variant for contrast, regardless of the experience colorMode. */}
      {experience.categories
        .filter((cat) => cat.text)
        .map((cat) => (
          <LegendRow
            indicatorFirst={stackedMobile}
            key={cat.id}
            label={cat.text!}
            indicatorColor={resolveCategoryColor(
              toPaletteColorName(cat.colorName),
              experience.colorPalette,
              ExperienceColorMode.Bold
            )}
          >
            <FeedIndicator />
          </LegendRow>
        ))}
    </Flex>
  );
};
